/**
 * Public /r payload for an executive brief leave-behind.
 *
 * The share link must not leak Funnel internals: sources, model, cache flags,
 * geo, selling-point provenance, or ROI assumption region. Prospects only
 * see the one-pager tiles + a thin company card.
 */
import type { ExecutiveBriefResult } from '../components/sales-enablement/executive-brief-types';

export interface PublicBriefSharePayload {
  kind: 'executive_brief';
  processName: string;
  brief?: {
    headline: string;
    addressedTo: string;
    companySnapshot: string;
    mainUseCase: { title: string; narrative: string };
    painToday: string[];
    moxoBenefits: Array<{ title: string; whyItMatters: string }>;
    sellingPoints: Array<{ point: string }>;
    recommendedNextStep: string;
    roiNarrative: string;
  };
  roi?: {
    scenario: 'conservative';
    annualHoursFreed: number;
    fteEquivalent: number;
    laborSavings: number;
    cycleDaysSaved: number;
  };
  enrichment?: {
    domain: string | null;
    industry: string | null;
    size: string | null;
    hqLocation: string | null;
  };
}

export function buildPublicBriefSharePayload(
  result: ExecutiveBriefResult,
  processName: string,
): PublicBriefSharePayload {
  const brief = result.brief;
  const e = result.enrichment;
  const roi = result.roi;
  const payload: PublicBriefSharePayload = {
    kind: 'executive_brief',
    processName,
  };
  if (brief) {
    payload.brief = {
      headline: brief.headline,
      addressedTo: brief.addressedTo,
      companySnapshot: brief.companySnapshot,
      mainUseCase: brief.mainUseCase,
      painToday: brief.painToday,
      moxoBenefits: brief.moxoBenefits,
      sellingPoints: brief.sellingPoints
        .filter((s) => s.source !== 'inferred')
        .map((s) => ({ point: s.point })),
      recommendedNextStep: brief.recommendedNextStep,
      roiNarrative: brief.roiNarrative,
    };
  }
  if (roi) {
    payload.roi = {
      scenario: roi.scenario,
      annualHoursFreed: roi.annualHoursFreed,
      fteEquivalent: roi.fteEquivalent,
      laborSavings: roi.laborSavings,
      cycleDaysSaved: roi.cycleDaysSaved,
    };
  }
  if (e) {
    payload.enrichment = {
      domain: e.domain ?? null,
      industry: e.industry ?? null,
      size: e.size ?? null,
      hqLocation: e.hqLocation ?? null,
    };
  }
  return payload;
}
